<template>
  <div class="checklist-section">
    <!-- Phase Switch (also minimize/expand) -->
    <div class="mb-2 d-flex">
      <b-button
        v-for="ph in phaseList"
        :key="ph"
        size="sm"
        class="mr-1 d-inline-flex align-items-center"
        :variant="selectedPhase === ph && !collapsed ? 'primary' : 'outline-primary'"
        @click="onPhaseClick(ph)"
      >
        {{ phaseButtonLabel(ph) }}
        <feather-icon
          v-if="selectedPhase === ph"
          :icon="collapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'"
          class="ml-1"
          size="14"
        />
      </b-button>
    </div>
    <b-row> 
      <b-col cols="12" class="pl-0">
        <b-card :class="['checklist-card','position-relative', { 'is-collapsed': collapsed }]">
          <b-collapse :visible="!collapsed" id="lead-checklist-body">
            <div class="d-flex align-items-center justify-content-between">
              <h3 class="checklist-title mb-1">Checklist de eficiência</h3>
            </div>

            <div class="checklist-content">
              <transition name="fade">
                <div v-if="isLoading" class="loading-overlay d-flex align-items-center justify-content-center">
                  <b-spinner small class="mr-2" /> Carregando...
                </div>
              </transition>

              <template v-if="!isLoading && visibleItems.length">
                <div
                  v-for="(it, idx) in visibleItems"
                  :key="it.item_id"
                  class="checklist-item d-flex justify-content-between align-items-center"
                >
                  <span class="checklist-text">
                    <strong>{{ idx + 1 }}.</strong> {{ it.name }}
                  </span>

                  <b-form-radio-group
                    v-if="it.type === 'boolean'"
                    v-model="it.uiValue"
                    :options="yesNoOptions"
                    :name="`bool-${it.item_id}`"
                    class="checklist-radio-inline"
                    :disabled="isLoading"
                    @change="onChange(it)"
                  />

                  <b-form-input
                    v-else-if="it.type === 'number'"
                    v-model.number="it.uiValue"
                    type="number"
                    :min="it.min"
                    :max="it.max"
                    step="0.0001"
                    :placeholder="placeholderFor(it)"
                    class="w-auto"
                    :disabled="isLoading"
                    @change="onChange(it)"
                    @blur="onChange(it)"
                  />

                  <b-form-select
                    v-else-if="it.type === 'scale'"
                    v-model.number="it.uiValue"
                    :options="scaleOptions(it)"
                    class="w-auto"
                    :disabled="isLoading"
                    @change="onChange(it)"
                  />

                  <b-form-textarea
                    v-else-if="it.type === 'text'"
                    v-model="it.uiValue"
                    rows="2"
                    :placeholder="placeholderFor(it)"
                    class="w-50"
                    :disabled="isLoading"
                    @change="onChange(it)"
                    @blur="onChange(it)"
                  />

                  <b-form-input
                    v-else-if="it.type === 'single_select'"
                    v-model="it.uiValue"
                    :placeholder="placeholderFor(it)"
                    class="w-auto"
                    :disabled="isLoading"
                    @change="onChange(it)"
                    @blur="onChange(it)"
                  />

                  <b-form-input
                    v-else-if="it.type === 'multi_select'"
                    v-model="it.uiValue"
                    placeholder="Separe por vírgula (ex.: A, B, C)"
                    class="w-50"
                    :disabled="isLoading"
                    @change="onChange(it)"
                    @blur="onChange(it)"
                  />

                  <span v-else class="text-danger small">Tipo não suportado: {{ it.type }}</span>
                </div>
              </template>

              <div v-else-if="!isLoading" class="text-muted py-2">
                Nenhum item disponível para esta fase.
              </div>
            </div>
          </b-collapse>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol,
  BFormRadioGroup, BFormInput, BFormSelect, BFormTextarea,
  BButton, BSpinner, BCollapse,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as types from "../store/types";

export default {
  name: "LeadChecklistDynamic",
  components: {
    BCard, BRow, BCol,
    BFormRadioGroup, BFormInput, BFormSelect, BFormTextarea,
    BButton, BSpinner, BCollapse,
  },
  props: {
    // Example: "EFFICIENCY_EA"
    checklistCode: { type: String, required: true },
    // optionally preselect a phase like "EFFICIENCY_EA-02"
    initialPhase: { type: String, default: null },
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      isLoading: false,
      items: [],             // [{ item_id, name, type, min, max, phase_code, answer, uiValue }]
      selectedPhase: null,
      collapsed: false,      // open by default
      yesNoOptions: [
        { text: "Sim", value: true },
        { text: "Não", value: false },
      ],
      savingMap: new Set(),  // item_ids currently saving (hook for spinners if you want)
    };
  },
  computed: {
    phaseList() {
      const s = new Set(this.items.map(i => i.phase_code).filter(Boolean));
      return Array.from(s).sort((a, b) => this.phaseOrder(a) - this.phaseOrder(b));
    },
    visibleItems() {
      const phase = this.selectedPhase;
      const list = this.items.filter(i => !phase || i.phase_code === phase);
      return list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    },
  },
  methods: {
    ...mapActions({
      getLeadChecklist: types.GET_LEAD_CHECKLIST,
      saveLeadChecklistItem: types.SAVE_LEAD_CHECKLIST_ITEM,
    }),

    // --- UI helpers ---
    phaseOrder(code) {
      const m = (code || "").match(/-(\d+)$/);
      return m ? Number(m[1]) : 0;
    },
    phaseButtonLabel(code) {
      const n = this.phaseOrder(code);
      if(n == 1){//Compromisso  
        return n ? `Fase ligação` : code;
      }else if(n == 2){//Prospecção
        return n ? `Fase preparação` : code;
      }else{
        return n ? `Fase ${n}` : code;
      }
    },
    onPhaseClick(ph) {
      if (this.selectedPhase === ph) {
        this.collapsed = !this.collapsed;   // toggle minimize/expand
      } else {
        this.selectedPhase = ph;            // switch phase
        this.collapsed = false;             // expand when switching
      }
    },

    // Placeholders & options
    placeholderFor(it) {
      if (it.type === "number" || it.type === "scale") {
        const min = it.min != null ? it.min : 0;
        const max = it.max != null ? `–${it.max}` : "";
        return `Digite um número ${max ? `(${min}${max})` : ""}`.trim();
      }
      if (it.type === "text") return "Digite um texto";
      return "";
    },
    scaleOptions(it) {
      const min = Number(it.min ?? 0);
      const max = Number(it.max ?? 10);
      const opts = [];
      for (let v = min; v <= max; v++) opts.push({ value: v, text: String(v) });
      return opts;
    },

    // Value normalization (fixes "8.0000" vs 8 and 1/0 vs true/false)
    toUiValue(item) {
      const ans = item.answer || {};
      switch (item.type) {
        case "boolean": {
          const v = ans.checked;
          if (v === null || v === undefined) return null;
          if (typeof v === "boolean") return v;
          const n = Number(v);
          return Number.isNaN(n) ? Boolean(v) : Boolean(n);
        }
        case "number":
        case "scale": {
          const v = ans.number_value;
          if (v === null || v === undefined || v === "") return null;
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        }
        default:
          return ans.text_value ?? "";
      }
    },
    toApiValue(item) {
      switch (item.type) {
        case "boolean": return item.uiValue;
        case "number":
        case "scale":  return item.uiValue === "" ? null : item.uiValue;
        default:       return item.uiValue ?? "";
      }
    },

    // --- Data flow ---
    async load() {
      const customerId = this.$route.params.id;
      if (!customerId) return;

      this.isLoading = true;
      try {
        const { data } = await this.getLeadChecklist({ customerId, code: this.checklistCode });
        const items = (data && data.items) ? data.items : [];
        this.items = items.map(it => ({ ...it, uiValue: this.toUiValue(it) }));
        const phases = this.phaseList;
        this.selectedPhase =
          this.initialPhase && phases.includes(this.initialPhase)
            ? this.initialPhase
            : (phases[0] || null);
      } finally {
        this.isLoading = false;
      }
    },

    makeAnswerPatch(item, value) {
      const now = new Date().toISOString();
      switch (item.type) {
        case "boolean": return { ...item.answer, checked: value, answered_at: now };
        case "number":
        case "scale":  return { ...item.answer, number_value: value, answered_at: now };
        default:       return { ...item.answer, text_value: value, answered_at: now };
      }
    },

    async onChange(item) {
      const customerId = this.$route.params.id;
      if (!customerId) return;

      const value = this.toApiValue(item);

      if (
        (item.type === "text" || item.type === "single_select" || item.type === "multi_select") &&
        (!value || (typeof value === "string" && value.trim() === ""))
      ) {
        return;
      }

      if ((item.type === "number" || item.type === "scale") && (value === null || value === "")) {
        return;
      }

      const prev = item.answer;

      // optimistic UI
      item.answer = this.makeAnswerPatch(item, value);
      this.savingMap.add(item.item_id);

      try {
        await this.saveLeadChecklistItem({ customerId, itemId: item.item_id, value });
        this.toast(
          {
            component: ToastificationContent,
            props: {
              title: "Salvando...",
              text: "Alguns itens foram atualizados",
              icon: "CheckIcon",
              variant: "success",
            },
          },
          {
            id: "checklist-save",
            timeout: 2000,
            closeOnClick: true,
            hideProgressBar: true,
          }
        );
      } catch (e) {
        // rollback on error
        item.answer = prev;
        item.uiValue = this.toUiValue(item);
        this.toast({
          component: ToastificationContent,
          props: { title: "Erro", text: "Erro ao salvar item. Tente novamente.", icon: "AlertTriangleIcon", variant: "danger" },
        });
      } finally {
        this.savingMap.delete(item.item_id);
      }
    }

  },

  async mounted() {
    await this.load();
  },
};
</script>

<style lang="scss" scoped>
.checklist-card {
  border-radius: 8px;
  transition: box-shadow 0.3s ease;
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
}
.checklist-card.is-collapsed > .card-body {
  padding: 0.1rem 1rem; /* optional tighter padding when collapsed */
}

.checklist-title { color: var(--body-color); font-weight: 600; padding-bottom: .5rem; }

.checklist-content {
  position: relative;
  .checklist-item {
    padding: 1rem 0; border-bottom: 1px dashed rgba(255,255,255,0.15);
    &:last-child { border-bottom: none; }
    .checklist-text { font-size: .9rem; color: var(--body-color); line-height: 1.4; flex: 1; margin-right: 1rem; }
    .checklist-radio-inline { flex-shrink: 0; }
  }
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.35);
  z-index: 2;
  color: #f26237;
  border-radius: 8px;
}
.fade-enter-active, .fade-leave-active { transition: opacity .15s; }
.fade-enter, .fade-leave-to { opacity: 0; }

@media (max-width: 768px) {
  .checklist-content .checklist-item {
    flex-direction: column; align-items: flex-start !important;
    .checklist-text { margin-bottom: .5rem; margin-right: 0; }
    .checklist-radio-inline { align-self: flex-end; }
  }
}
</style>
